import { useRef, useCallback } from "react";

/**
 * useFormDraft — Persistance d'un brouillon de formulaire (anti-perte de saisie).
 *
 * Objectif métier :
 *  Le brouillon doit SURVIVRE à la navigation ET au rechargement de page,
 *  jusqu'à ce que l'action finale soit accomplie (compte créé / annonce soumise /
 *  disponibilité mise à jour) — moment où `clearDraft()` est appelé.
 *  Exemple : l'utilisateur remplit la pop-up → est redirigé vers /inscription →
 *  crée son compte → revient → il doit retrouver toutes ses données.
 *
 * Contrainte technique :
 *  L'app tourne dans une IFRAME d'aperçu (preview--*.lumi.ing). Dans une iframe,
 *  `localStorage`/`sessionStorage` sont souvent BLOQUÉS (partitionnement du
 *  stockage, blocage cookies tiers, mode privé). Si on ne s'appuie que sur
 *  `localStorage`, rien n'est jamais écrit ni relu → bug observé.
 *
 * Solution — persistance multi-couches résiliente (par ordre de fiabilité de
 * lecture, écriture sur TOUTES les couches disponibles) :
 *  1) localStorage  → persiste entre rechargements ET sessions.
 *  2) sessionStorage → persiste au rechargement de l'onglet (survit à la navigation).
 *  3) cookie first-party → survit au rechargement même quand localStorage est
 *     partitionné dans une iframe (les cookies de 1ʳᵉ partie restent souvent
 *     accessibles), durée de vie 7 jours.
 *  4) cache mémoire module-level → dernier recours (ne survit pas au rechargement,
 *     mais couvre fermeture/réouverture rapide de la pop-up dans le même onglet).
 *
 * Tant que `clearDraft()` n'est pas appelé, le brouillon reste donc disponible
 * sur au moins une couche, garantissant la restauration après navigation/recharge.
 */

// Cache mémoire partagé entre tous les montages (dernier recours).
const memoryStore = new Map<string, string>();

const COOKIE_PREFIX = "draft_";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 jours

function cookieKey(key: string): string {
  // On encode la clé pour qu'elle soit un nom de cookie valide.
  return COOKIE_PREFIX + encodeURIComponent(key);
}

function readCookie(key: string): string | null {
  try {
    const name = cookieKey(key) + "=";
    const parts = document.cookie ? document.cookie.split("; ") : [];
    for (const part of parts) {
      if (part.startsWith(name)) {
        return decodeURIComponent(part.slice(name.length));
      }
    }
  } catch {
    // document.cookie indisponible → ignore.
  }
  return null;
}

function writeCookie(key: string, value: string): void {
  const encoded = encodeURIComponent(value);
  // En iframe d'aperçu (preview--*.lumi.ing), le contexte est cross-site :
  // un cookie SameSite=Lax y est traité comme cookie tiers et REJETÉ.
  // Pour qu'il survive à la navigation/rechargement dans l'iframe, il faut
  // SameSite=None; Secure. On tente d'abord cette variante, avec repli Lax
  // pour les contextes non-HTTPS où Secure serait refusé.
  try {
    document.cookie = `${cookieKey(
      key
    )}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=None; Secure`;
  } catch {
    // ignore — on tente le repli ci-dessous
  }
  try {
    document.cookie = `${cookieKey(
      key
    )}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    // silencieux
  }
}

function removeCookie(key: string): void {
  try {
    document.cookie = `${cookieKey(key)}=; path=/; max-age=0; SameSite=None; Secure`;
  } catch {
    // ignore
  }
  try {
    document.cookie = `${cookieKey(key)}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // silencieux
  }
}

function readRaw(key: string): string | null {
  // Ordre de fiabilité : localStorage → sessionStorage → cookie → mémoire.
  try {
    const ls = localStorage.getItem(key);
    if (ls != null) return ls;
  } catch {
    // bloqué
  }
  try {
    const ss = sessionStorage.getItem(key);
    if (ss != null) return ss;
  } catch {
    // bloqué
  }
  const ck = readCookie(key);
  if (ck != null) return ck;

  return memoryStore.has(key) ? (memoryStore.get(key) as string) : null;
}

function writeRaw(key: string, value: string): void {
  // On écrit sur TOUTES les couches disponibles pour maximiser la survie.
  memoryStore.set(key, value);
  try {
    localStorage.setItem(key, value);
  } catch {
    // bloqué
  }
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // bloqué
  }
  writeCookie(key, value);
}

function removeRaw(key: string): void {
  memoryStore.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // bloqué
  }
  try {
    sessionStorage.removeItem(key);
  } catch {
    // bloqué
  }
  removeCookie(key);
}

export function useFormDraft<T extends Record<string, unknown>>(
  key: string,
  options?: { enabled?: boolean }
): {
  initialDraft: Partial<T> | null;
  saveDraft: (values: T) => void;
  clearDraft: () => void;
} {
  const enabled = options?.enabled ?? true;

  // Lecture unique du brouillon existant (au tout premier rendu, synchrone).
  const initialDraftRef = useRef<Partial<T> | null | undefined>(undefined);
  if (initialDraftRef.current === undefined) {
    let parsed: Partial<T> | null = null;
    if (enabled) {
      try {
        const raw = readRaw(key);
        if (raw) parsed = JSON.parse(raw) as Partial<T>;
      } catch {
        parsed = null;
      }
    }
    initialDraftRef.current = parsed;
  }

  // Une fois nettoyé (après l'action finale), on n'écrit plus le brouillon.
  const clearedRef = useRef(false);

  const saveDraft = useCallback(
    (values: T) => {
      if (!enabled || clearedRef.current) return;
      try {
        writeRaw(key, JSON.stringify(values));
      } catch {
        // silencieux (valeur non sérialisable, etc.)
      }
    },
    [key, enabled]
  );

  const clearDraft = useCallback(() => {
    clearedRef.current = true;
    removeRaw(key);
  }, [key]);

  return { initialDraft: initialDraftRef.current, saveDraft, clearDraft };
}
